// src/lib/db.js
// Firestore structure:
//   /orgs/{orgId}/items/{itemId}               — shared task pool
//   /orgs/{orgId}/members/{uid}                — team roster
//   /orgs/{orgId}/invites/{inviteId}           — pending invites
//   /orgs/{orgId}/taskTemplates/{id}           — reusable task templates
//   /orgs/{orgId}/eventBundles/{id}            — event bundles (ordered task sets)
//   /orgs/{orgId}/eventInstances/{id}          — fired event records
//   /orgs/{orgId}/customCategories/{slug}      — custom categories
//   /orgs/{orgId}/incoming/{id}                — escalations from Practice Comms, awaiting triage
//   /users/{uid}/fcmTokens/{token}             — per-device push tokens

import {
  collection, doc, addDoc, updateDoc, deleteDoc, setDoc, getDoc,
  onSnapshot, query, orderBy, getDocs, writeBatch, where,
  serverTimestamp
} from "firebase/firestore";
import { db, ORG_ID } from "./firebase";
import { SEED_ITEMS } from "./seedData";

// ── Refs ──────────────────────────────────────────────────────────────────
const orgRef         = () => doc(db, "orgs", ORG_ID);
const itemsRef       = () => collection(db, "orgs", ORG_ID, "items");
const membersRef     = () => collection(db, "orgs", ORG_ID, "members");
const invitesRef     = () => collection(db, "orgs", ORG_ID, "invites");
const customCatsRef  = () => collection(db, "orgs", ORG_ID, "customCategories");
const incomingRef    = () => collection(db, "orgs", ORG_ID, "incoming");
const tokensRef      = (uid) => collection(db, "users", uid, "fcmTokens");

// ── Org setup ─────────────────────────────────────────────────────────────

// Called on first owner sign-in. Creates org + seeds items if needed.
export async function setupOrgIfNeeded(user) {
  const orgSnap = await getDoc(orgRef());
  if (!orgSnap.exists()) {
    // Create org
    await setDoc(orgRef(), {
      name: "Paradise Creek Counseling & Consulting",
      createdAt: serverTimestamp(),
      ownerUid: user.uid,
    });
    // Add owner as first member
    await setDoc(doc(membersRef(), user.uid), {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName || user.email,
      photoURL: user.photoURL || null,
      permission: "owner",
      allowedRoles: ["pcc", "pyha"],
      joinedAt: serverTimestamp(),
    });
    // Seed initial items
    const batch = writeBatch(db);
    SEED_ITEMS.forEach(item => {
      batch.set(doc(itemsRef()), {
        ...item,
        assignedTo: user.uid,   // assign seed items to owner by default
        createdBy: user.uid,
        createdAt: serverTimestamp(),
      });
    });
    await batch.commit();
  }
  // Seed event bundles if they don't exist yet (idempotent, silently skipped for staff)
  try { await seedBundlesIfNeeded(user.uid); } catch {}
}

// ── Member management ──────────────────────────────────────────────────────

export function subscribeToMembers(callback) {
  return onSnapshot(membersRef(), snap => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
}

export async function getMember(uid) {
  const snap = await getDoc(doc(membersRef(), uid));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function updateMember(uid, updates) {
  return updateDoc(doc(membersRef(), uid), updates);
}

export async function removeMember(uid) {
  return deleteDoc(doc(membersRef(), uid));
}

// ── Invites ───────────────────────────────────────────────────────────────

export async function createInvite(email, permission, allowedRoles, invitedBy) {
  const key = email.toLowerCase();
  const existing = await getDoc(doc(invitesRef(), key));
  if (existing.exists() && existing.data().status === "pending") {
    throw new Error("An invite is already pending for this email.");
  }
  await setDoc(doc(invitesRef(), key), {
    email: key,
    permission,
    allowedRoles,
    invitedBy,
    status: "pending",
    createdAt: serverTimestamp(),
  });
  return key;
}

export async function getInvites() {
  const snap = await getDocs(query(invitesRef(), where("status", "==", "pending")));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function cancelInvite(inviteId) {
  return updateDoc(doc(invitesRef(), inviteId), { status: "cancelled" });
}

// Called when a new user signs in — checks if their email has a pending invite
export async function checkAndAcceptInvite(user) {
  let inviteDoc;
  try {
    inviteDoc = await getDoc(doc(invitesRef(), user.email.toLowerCase()));
  } catch {
    return null;
  }
  if (!inviteDoc.exists() || inviteDoc.data().status !== "pending") return null;

  const invite = inviteDoc.data();

  // Add them as a member
  await setDoc(doc(membersRef(), user.uid), {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName || user.email,
    photoURL: user.photoURL || null,
    permission: invite.permission,
    allowedRoles: invite.allowedRoles,
    joinedAt: serverTimestamp(),
  });

  // Mark invite as accepted
  await updateDoc(doc(invitesRef(), user.email.toLowerCase()), {
    status: "accepted",
    acceptedAt: serverTimestamp(),
    acceptedBy: user.uid,
  });

  return invite;
}

// ── Incoming escalations from Practice Comms ───────────────────────────────
//
// GOVERNED BY functions/CONTRACT.md. The Cloud Function writes these documents
// and is the only thing that can create one; a triager's single permitted
// operation is deciding a pending item, and the security rules enforce exactly
// that field list.
//
// NOTE what is NOT here: no create, no delete, and no write of `itemId`. That
// field is the function's attestation that a real item exists, and Practice
// Comms resolves a patient communication when it sees one — so it is written
// server-side by the trigger and nowhere else. Deleting an incoming item is
// what Comms reports as `orphaned`, which is why the rules refuse it.

/** Every pending escalation. A single-field equality query, so it needs no
 *  composite index. Ordering is done in memory — there are a handful of these
 *  at a time, and an orderBy here would need an index deployed before the inbox
 *  worked at all. */
export function subscribeToPendingIncoming(callback) {
  const q = query(incomingRef(), where("status", "==", "pending"));
  return onSnapshot(q, snap => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
}

/** Recently decided escalations, so a triager can see what they did — and so a
 *  rejection's reason stays readable here, since that reason is what the
 *  Practice Comms front desk is now looking at. */
export function subscribeToDecidedIncoming(callback) {
  const q = query(incomingRef(), where("status", "in", ["accepted", "rejected"]));
  return onSnapshot(q, snap => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
}

/** Accept an escalation. This does NOT create the item — the trigger does, and
 *  then stamps `itemId`. Until that stamp lands the inbox must not tell anyone
 *  the work was handed off, for the same reason getTask keeps reporting
 *  `pending`: a confirmation the mechanism has not earned is a false record
 *  whether the reader is a system or a person. */
export async function acceptIncoming(incomingId, { assignedTo, due, category, triagedBy }) {
  return updateDoc(doc(incomingRef(), incomingId), {
    status: "accepted",
    assignedTo,
    due,
    category,
    triagedBy,
    triagedAt: serverTimestamp(),
  });
}

/** Reject an escalation with a reason that travels back to Practice Comms and
 *  is shown to whoever picks the communication up. Bounded at 280 by the
 *  contract, by the rules, and by the form. */
export async function rejectIncoming(incomingId, { reason, triagedBy }) {
  return updateDoc(doc(incomingRef(), incomingId), {
    status: "rejected",
    rejectionReason: reason,
    triagedBy,
    triagedAt: serverTimestamp(),
  });
}

/** Correct the fields of an acceptance the trigger could not honour.
 *
 *  The rules only permit a transition out of `pending`, so this is deliberately
 *  NOT a status change — the document is already `accepted`, and this rewrites
 *  the triage fields so the trigger can run again. Without it, an acceptance
 *  with a bad due date is stuck: no item, no itemId, and getTask reporting
 *  `pending` forever with nobody able to see why. */
export async function correctIncomingTriage(incomingId, { assignedTo, due, category }) {
  return updateDoc(doc(incomingRef(), incomingId), { assignedTo, due, category });
}

// ── Items ──────────────────────────────────────────────────────────────────

export function subscribeToItems(callback) {
  const q = query(itemsRef(), orderBy("due", "asc"));
  return onSnapshot(q, snap => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
}

// Constrained query for external users — only their own PYHA tasks.
// The where clauses are required so Firestore security rules can validate the query.
export function subscribeToExternalItems(uid, callback) {
  const q = query(
    itemsRef(),
    where("assignedTo", "==", uid),
    where("roleId", "==", "pyha"),
    orderBy("due", "asc")
  );
  return onSnapshot(q, snap => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
}

export async function addItem(item, createdBy) {
  return addDoc(itemsRef(), {
    ...item,
    createdBy,
    createdAt: serverTimestamp(),
  });
}

export async function updateItem(itemId, updates) {
  return updateDoc(doc(itemsRef(), itemId), { ...updates, updatedAt: serverTimestamp() });
}

export async function deleteItem(itemId) {
  return deleteDoc(doc(itemsRef(), itemId));
}

export async function uncompleteItem(itemId) {
  return updateItem(itemId, { completed: false, completedBy: null, completedAt: null });
}

export async function completeItem(item, uid) {
  await updateItem(item.id, { completed: true, completedBy: uid, completedAt: serverTimestamp() });
  if (item.recurrence !== "none") {
    const nextDue = addRecurrence(item.due, item.recurrence);
    if (nextDue) {
      await addItem(
        { ...item, id: undefined, due: nextDue, completed: false, completedBy: null, completedAt: null },
        uid
      );
    }
  }
}

function addRecurrence(dateStr, recurrence) {
  if (!dateStr || recurrence === "none") return null;
  const d = new Date(dateStr);
  switch (recurrence) {
    case "weekly":    d.setDate(d.getDate() + 7); break;
    case "biweekly":  d.setDate(d.getDate() + 14); break;
    case "monthly":   d.setMonth(d.getMonth() + 1); break;
    case "quarterly": d.setMonth(d.getMonth() + 3); break;
    case "annually":  d.setFullYear(d.getFullYear() + 1); break;
    default: return null;
  }
  return d.toISOString().split("T")[0];
}

// ── FCM Tokens ────────────────────────────────────────────────────────────

export async function saveFcmToken(uid, token) {
  return setDoc(doc(tokensRef(uid), token), {
    token, updatedAt: serverTimestamp(), platform: navigator.platform
  });
}

// ── Custom categories ─────────────────────────────────────────────────────

export function subscribeToCustomCategories(callback) {
  return onSnapshot(customCatsRef(), snap => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
}

export async function addCustomCategory(roleId, label, color) {
  const slug = label.toLowerCase().replace(/\s+/g, "_");
  return setDoc(doc(customCatsRef(), slug), { roleId, label, color, createdAt: serverTimestamp() });
}

export async function deleteCustomCategory(docId) {
  return deleteDoc(doc(customCatsRef(), docId));
}

// ── Task Library refs ──────────────────────────────────────────────────────
const taskTemplatesRef  = () => collection(db, "orgs", ORG_ID, "taskTemplates");
const eventBundlesRef   = () => collection(db, "orgs", ORG_ID, "eventBundles");
const eventInstancesRef = () => collection(db, "orgs", ORG_ID, "eventInstances");

// ── Task Templates ────────────────────────────────────────────────────────

export function subscribeToTaskTemplates(callback) {
  return onSnapshot(taskTemplatesRef(), snap => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
}
export async function addTaskTemplate(template, uid) {
  return addDoc(taskTemplatesRef(), { ...template, createdBy: uid, createdAt: serverTimestamp() });
}
export async function updateTaskTemplate(id, updates) {
  return updateDoc(doc(taskTemplatesRef(), id), { ...updates, updatedAt: serverTimestamp() });
}
export async function deleteTaskTemplate(id) {
  return deleteDoc(doc(taskTemplatesRef(), id));
}

// ── Event Bundles ─────────────────────────────────────────────────────────

export function subscribeToEventBundles(callback) {
  return onSnapshot(eventBundlesRef(), snap => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
}
export async function addEventBundle(bundle, uid) {
  return addDoc(eventBundlesRef(), { ...bundle, createdBy: uid, createdAt: serverTimestamp() });
}
export async function updateEventBundle(id, updates) {
  return updateDoc(doc(eventBundlesRef(), id), { ...updates, updatedAt: serverTimestamp() });
}
export async function deleteEventBundle(id) {
  return deleteDoc(doc(eventBundlesRef(), id));
}

// Fire an event bundle — creates all tasks + an event instance record in one batch
export async function fireEvent(bundle, anchorDate, contextName, uid, members) {
  const ownerMember   = members.find(m => m.permission === "owner");
  const managerMember = members.find(m => m.permission === "manager");

  const resolveAssignee = (role) => {
    if (!role)             return "";
    if (role === "owner")   return ownerMember?.uid   || "";
    if (role === "manager") return managerMember?.uid || "";
    return role; // already a UID
  };

  const anchor = new Date(anchorDate + "T00:00:00");
  const batch  = writeBatch(db);

  for (const task of (bundle.tasks || [])) {
    const due = new Date(anchor);
    due.setDate(due.getDate() + task.dayOffset);
    const dueStr = due.toISOString().split("T")[0];
    batch.set(doc(itemsRef()), {
      title:      contextName ? `${task.title} — ${contextName}` : task.title,
      category:   task.category,
      roleId:     bundle.roleId,
      notes:      task.notes || "",
      due:        dueStr,
      recurrence: "none",
      completed:  false,
      assignedTo: resolveAssignee(task.defaultAssignedTo),
      createdBy:  uid,
      createdAt:  serverTimestamp(),
      sourceType: "event",
    });
  }

  const instanceRef = doc(eventInstancesRef());
  batch.set(instanceRef, {
    bundleId:    bundle.id,
    bundleName:  bundle.name,
    anchorDate,
    contextName: contextName || "",
    firedBy:     uid,
    firedAt:     serverTimestamp(),
    status:      "active",
  });

  await batch.commit();
  return instanceRef.id;
}

// Pull a single template onto the board as a new task
export async function pullFromLibrary(template, dueDate, assignedTo, uid) {
  return addDoc(itemsRef(), {
    title:      template.title,
    category:   template.category,
    roleId:     template.roleId,
    notes:      template.notes || "",
    due:        dueDate,
    recurrence: "none",
    completed:  false,
    assignedTo: assignedTo || "",
    createdBy:  uid,
    createdAt:  serverTimestamp(),
    sourceType: "library",
    templateId: template.id,
  });
}

// ── Bundle seeding ────────────────────────────────────────────────────────
// Called from setupOrgIfNeeded after the org/member exist.
// Idempotent — skips if any bundles already exist.

const SEED_BUNDLES = [
  {
    name: "New Provider Hire",
    description: "Onboarding checklist for a new clinical provider",
    roleId: "pcc",
    anchorLabel: "Start date",
    tasks: [
      { title: "NPI and license verification",           category: "credentialing", dayOffset: -14, defaultAssignedTo: "manager", notes: "" },
      { title: "CAQH ProView setup",                     category: "credentialing", dayOffset: -7,  defaultAssignedTo: "manager", notes: "" },
      { title: "Malpractice certificate collected",       category: "credentialing", dayOffset: -7,  defaultAssignedTo: "manager", notes: "" },
      { title: "Gusto onboarding initiated",             category: "hr",            dayOffset: -7,  defaultAssignedTo: "manager", notes: "" },
      { title: "Practice Brain invitation sent",          category: "onboarding",    dayOffset: -5,  defaultAssignedTo: "manager", notes: "" },
      { title: "Module 2 orientation meeting",           category: "onboarding",    dayOffset: 0,   defaultAssignedTo: "manager", notes: "" },
      { title: "PracticeQ training complete",            category: "onboarding",    dayOffset: 5,   defaultAssignedTo: "",        notes: "Provider completes this independently" },
      { title: "HIPAA acknowledgment signed",            category: "compliance",    dayOffset: 14,  defaultAssignedTo: "manager", notes: "" },
      { title: "Mandated reporting acknowledgment signed", category: "compliance",  dayOffset: 14,  defaultAssignedTo: "manager", notes: "" },
      { title: "CAQH profile complete and attested",     category: "credentialing", dayOffset: 7,   defaultAssignedTo: "manager", notes: "" },
      { title: "Blue Cross enrollment submitted",        category: "credentialing", dayOffset: 7,   defaultAssignedTo: "manager", notes: "" },
      { title: "Idaho Medicaid enrollment submitted",    category: "credentialing", dayOffset: 7,   defaultAssignedTo: "manager", notes: "" },
    ],
  },
  {
    name: "Employee Offboarding",
    description: "Steps for separating from a staff or provider employee",
    roleId: "pcc",
    anchorLabel: "Last day",
    tasks: [
      { title: "Schedule exit interview",                category: "hr",            dayOffset: -14, defaultAssignedTo: "owner",   notes: "" },
      { title: "Reassign active client caseload",        category: "hr",            dayOffset: -7,  defaultAssignedTo: "owner",   notes: "" },
      { title: "Remove from PracticeQ scheduling",       category: "onboarding",    dayOffset: -1,  defaultAssignedTo: "manager", notes: "" },
      { title: "Revoke Practice Brain access",           category: "onboarding",    dayOffset: 0,   defaultAssignedTo: "owner",   notes: "" },
      { title: "Remove CAQH access if shared",           category: "credentialing", dayOffset: 0,   defaultAssignedTo: "manager", notes: "" },
      { title: "Process final pay in Gusto",             category: "hr",            dayOffset: 0,   defaultAssignedTo: "manager", notes: "" },
      { title: "Collect any practice keys or materials", category: "hr",            dayOffset: 0,   defaultAssignedTo: "manager", notes: "" },
      { title: "Notify active payers of departure",      category: "credentialing", dayOffset: 1,   defaultAssignedTo: "manager", notes: "" },
      { title: "Update credential tracking spreadsheet", category: "credentialing", dayOffset: 1,   defaultAssignedTo: "manager", notes: "" },
    ],
  },
  {
    name: "Medicaid Audit Response",
    description: "Response protocol for Idaho Medicaid audit notices",
    roleId: "pcc",
    anchorLabel: "Audit notice date",
    tasks: [
      { title: "Notify owner and supervisor",            category: "compliance",    dayOffset: 0,  defaultAssignedTo: "manager", notes: "" },
      { title: "Pull requested client records",          category: "compliance",    dayOffset: 1,  defaultAssignedTo: "manager", notes: "" },
      { title: "Review documentation for completeness",  category: "compliance",    dayOffset: 2,  defaultAssignedTo: "owner",   notes: "" },
      { title: "Consult with compliance attorney",       category: "compliance",    dayOffset: 3,  defaultAssignedTo: "owner",   notes: "If needed" },
      { title: "Prepare audit response package",         category: "compliance",    dayOffset: 5,  defaultAssignedTo: "manager", notes: "" },
      { title: "Submit response to Medicaid",            category: "billing",       dayOffset: 7,  defaultAssignedTo: "manager", notes: "" },
      { title: "Document audit outcome in compliance file", category: "compliance", dayOffset: 30, defaultAssignedTo: "manager", notes: "" },
    ],
  },
  {
    name: "Facilities Incident",
    description: "Response protocol for physical location incidents",
    roleId: "pcc",
    anchorLabel: "Incident date",
    tasks: [
      { title: "Document incident details",              category: "vendors",    dayOffset: 0,  defaultAssignedTo: "manager", notes: "" },
      { title: "Notify landlord",                        category: "vendors",    dayOffset: 0,  defaultAssignedTo: "manager", notes: "If applicable" },
      { title: "File insurance claim",                   category: "vendors",    dayOffset: 1,  defaultAssignedTo: "owner",   notes: "If applicable" },
      { title: "Arrange repairs",                        category: "vendors",    dayOffset: 1,  defaultAssignedTo: "manager", notes: "" },
      { title: "Follow up on repair completion",         category: "vendors",    dayOffset: 7,  defaultAssignedTo: "manager", notes: "" },
      { title: "Update facilities log",                  category: "vendors",    dayOffset: 7,  defaultAssignedTo: "manager", notes: "" },
    ],
  },
  {
    name: "New Admin Hire",
    description: "Onboarding checklist for a new administrative staff member",
    roleId: "pcc",
    anchorLabel: "Start date",
    tasks: [
      { title: "Gusto onboarding initiated",             category: "hr",         dayOffset: -7, defaultAssignedTo: "manager", notes: "" },
      { title: "Practice Brain invitation sent",          category: "onboarding", dayOffset: -5, defaultAssignedTo: "manager", notes: "" },
      { title: "PracticeQ access configured",            category: "onboarding", dayOffset: -3, defaultAssignedTo: "manager", notes: "" },
      { title: "30-day check-in scheduled",              category: "hr",         dayOffset: 0,  defaultAssignedTo: "owner",   notes: "" },
      { title: "HIPAA acknowledgment signed",            category: "compliance", dayOffset: 7,  defaultAssignedTo: "manager", notes: "" },
      { title: "Mandated reporting acknowledgment signed", category: "compliance", dayOffset: 7, defaultAssignedTo: "manager", notes: "" },
    ],
  },
];

async function seedBundlesIfNeeded(uid) {
  const snap = await getDocs(eventBundlesRef());
  if (!snap.empty) return;
  const batch = writeBatch(db);
  SEED_BUNDLES.forEach(bundle => {
    batch.set(doc(eventBundlesRef()), { ...bundle, createdBy: uid, createdAt: serverTimestamp() });
  });
  await batch.commit();
}
